import React, {Fragment} from 'react'
import autolinker from 'autolinker'
import _ from 'lodash'

const defaultLinkRender = (match, key) => {
  const spotify = match.matchedText.match(/open\.spotify\.com\/([\w/]+)\??/i)
  if (spotify) {
    return <Fragment key={key}>
      <br />
      <iframe
        src={`https://open.spotify.com/embed/${spotify[1]}`}
        width='100%'
        height='80'
        frameBorder='0'
        allowtransparency='true'
        allow='encrypted-media' />
      <br />
    </Fragment>
  }
  return <a key={key} href={match.getAnchorHref()} rel='noopener' target='_blank'>{match.getAnchorText()}</a>
}

// takes text as input, returns a <Fragment /> with anchors
const autolink = (text, render = defaultLinkRender) => {
  // here's how we efficiently render links without using dangerouslySetInnerHTML
  // 1. use autolinker to parse the links out of the provided text
  const matches = autolinker.parse(text)

  // 2. using the offset data provided by autolinker, chop the source text into
  //    {left, match, ...rest} partitions for each match, applying link rendering
  //    to the match portion
  const chunks = matches.reduce(({arr, rest, offsetOffset}, match, index) => {
    const offset = match.offset - offsetOffset
    const matchLength = match.matchedText.length
    const left = rest.slice(0, offset)
    const m = render(match, index)
    const _rest = rest.slice(offset + matchLength)
    return {arr: arr.concat([left, m]), rest: _rest, offsetOffset: match.offset + matchLength}
  }, {arr: [], rest: text, offsetOffset: 0})

  // 3. once all of the matches have been consumed in the reduce function,
  //    anything remaining in the ...rest partition is guaranteed to be plaintext
  return chunks.arr.concat(chunks.rest)
}

const defaultOpts = {
  autolink: true
}

// formatText() will convert single \n into <br />
// and make \n\n split the text into paragraphs
export const formatText = (text, _opts = {}) => {
  const opts = {...defaultOpts, ..._opts}

  if (!text) return

  // This code mimics ridewithgps utils.js:formatText()
  const cleaned = text.replace(/\r\n?/g, '\n').replace(/\n+$/, '').replace(/^\n+/, '')
  const paragraphs = cleaned.split(/\n\n+/g)
  const paragraphsWithBr = paragraphs.map(p => nlToBr(p, opts))

  return paragraphsWithBr.map((p, i) => <p key={i}>{p}</p>)
}

export const nlToBr = (text, opts = {}) => {
  if (!text) return

  const brs = text.split(/\n+/g)

  return brs.map((b, i) => (
    <Fragment key={i}>
      {opts.autolink ? autolink(b) : b}
      {i + 1 < brs.length && <br />}
    </Fragment>
  ))
}

// pluralize a string, optionally including the count.
// Usage Examples
// pl(12, 'Result') => '12 Results'
// pl(1, 'Result') => '1 Result'
// pl(1, 'Octop', {suffixSingle: 'us', suffixMultiple: 'i'}) => '1 Octopus'
// pl(3, 'Octop', {suffixSingle: 'us', suffixMultiple: 'i'}) => '3 Octopi'
// see also: https://www.youtube.com/watch?v=n4PWP8uL-1o
export const pl = (count, string, {suffixMultiple = 's', suffixSingle = '', includeCount = true} = {}) => {
  const label = count === 1
    ? string + suffixSingle
    : string + suffixMultiple

  return includeCount
    ? `${count} ${label}`
    : label
}

export const j = (values, separator = ', ') => {
  return values && values.filter(v => !!v).join(separator)
}

export const pascalCase = text => _.capitalize(_.camelCase(text))
